// frontend/src/services/auth/authService.js
import { createClient } from '@supabase/supabase-js';
import api from '../api';
import errorService from '../error/errorService';
import { getToken, setToken, removeToken } from './tokenService';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || "https://inzunrnptwwiibttfcfx.supabase.co";
const supabaseKey = process.env.REACT_APP_SUPABASE_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImluenVucm5wdHd3aWlidHRmY2Z4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDU2MTgzNTUsImV4cCI6MjA2MTE5NDM1NX0.tlFizbfm91yGyZJnlyQgFuU1kq9DLu8j2NmhTdjkOgI";

const supabase = createClient(supabaseUrl, supabaseKey);

/**
 * Authentication Service - Handles all authentication-related operations
 */
class AuthService {
  constructor() {
    this.refreshTimerId = null;
    this.listeners = [];
  }

  /**
   * Initialize the auth service
   * Sets up auth state change listeners and session management
   * @returns {Promise} - Promise that resolves when initialization completes
   */
  async initialize() {
    try {
      // Check for existing session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        // Set token in storage and API headers
        setToken(session.access_token);
        
        // Setup session refresh
        this._setupSessionRefresh(session);
        
        // Notify listeners of auth state change
        this._notifyListeners({ type: 'SIGNED_IN', user: session.user });
      }
      
      // Set up auth state change listener
      const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
        if (event === 'SIGNED_IN') {
          setToken(session.access_token);
          this._setupSessionRefresh(session);
          this._notifyListeners({ type: 'SIGNED_IN', user: session.user });
        } else if (event === 'SIGNED_OUT') {
          removeToken();
          this._clearSessionRefresh();
          this._notifyListeners({ type: 'SIGNED_OUT' });
        } else if (event === 'TOKEN_REFRESHED') {
          setToken(session.access_token);
          this._setupSessionRefresh(session);
          this._notifyListeners({ type: 'TOKEN_REFRESHED', user: session.user });
        }
      });
      
      // Store the listener for cleanup
      this.authStateSubscription = authListener?.subscription;
      
      return { initialized: true };
    } catch (error) {
      console.error("Auth initialization error:", error);
      return { initialized: false, error };
    }
  }

  /**
   * Sign in with Google
   * @returns {Promise} - Promise that resolves when OAuth flow starts
   */
  async signInWithGoogle() {
    try {
      const redirectUrl = window.location.origin + (window.location.pathname === "/" ? "" : window.location.pathname);
      
      // Start the Google OAuth flow
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          skipBrowserRedirect: true,
          queryParams: {
            prompt: 'select_account',
            access_type: 'offline'
          }
        }
      });
      
      // If we got a URL back, redirect the browser
      if (data && data.url) {
        window.location.href = data.url;
        return { started: true };
      } else if (error) {
        return errorService.handleAuthError(error);
      }
    } catch (error) {
      return errorService.handleAuthError(error);
    }
  }

  /**
   * Sign out the current user
   * @returns {Promise} - Promise that resolves when sign out completes
   */
  async signOut() {
    try {
      await supabase.auth.signOut();
      removeToken();
      this._clearSessionRefresh();
      return { success: true };
    } catch (error) {
      return errorService.handleAuthError(error);
    }
  }

  /**
   * Get the current user
   * @returns {Promise} - Promise that resolves with the current user
   */
  async getCurrentUser() {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error) throw error;
      
      return user;
    } catch (error) {
      console.error("Error getting current user:", error);
      return null;
    }
  }

  /**
   * Check if the user is authenticated
   * @returns {Promise<boolean>} - Promise that resolves with authentication status
   */
  async isAuthenticated() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return !!session;
    } catch (error) {
      console.error("Error checking authentication:", error);
      return false;
    }
  }

  /**
   * Add auth state change listener
   * @param {Function} listener - Function to call on auth state changes
   * @returns {Function} - Function to remove the listener
   */
  addAuthStateChangeListener(listener) {
    this.listeners.push(listener);
    
    // Return function to remove listener
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Clean up resources
   */
  cleanup() {
    this._clearSessionRefresh();
    
    if (this.authStateSubscription) {
      this.authStateSubscription.unsubscribe();
    }
    
    this.listeners = [];
  }

  /**
   * Refresh the authentication token
   * @private
   */
  async _refreshToken() {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error("Failed to refresh session:", error);
        
        // If refresh fails, check if we're still authenticated
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          // If no valid session, sign out
          this._notifyListeners({ type: 'SIGNED_OUT' });
        }
      } else if (data.session) {
        // Update token in storage
        setToken(data.session.access_token);
        
        // Setup refresh for the new session
        this._setupSessionRefresh(data.session);
        
        // Notify listeners
        this._notifyListeners({ type: 'TOKEN_REFRESHED', user: data.session.user });
      }
    } catch (err) {
      console.error("Error refreshing token:", err);
    }
  }

  /**
   * Setup session refresh timer
   * @param {Object} session - The session object
   * @private
   */
  _setupSessionRefresh(session) {
    // Clear any existing timer
    this._clearSessionRefresh();
    
    if (session) {
      try {
        // Get expiry time from session
        const expiresAt = new Date(session.expires_at * 1000);
        const now = new Date();
        
        // Calculate refresh time (5 minutes before expiry)
        const timeUntilExpiry = expiresAt - now;
        const refreshTime = timeUntilExpiry - (5 * 60 * 1000); // 5 minutes before expiry
        
        if (refreshTime > 0) {
          // Set timeout to refresh token
          this.refreshTimerId = setTimeout(() => {
            this._refreshToken();
          }, refreshTime);
          
          console.log(`Token refresh scheduled in ${Math.round(refreshTime / 60000)} minutes`);
        } else {
          // Token is about to expire or has expired, refresh now
          this._refreshToken();
        }
      } catch (err) {
        console.error("Error setting up session refresh:", err);
      }
    }
  }

  /**
   * Clear the session refresh timer
   * @private
   */
  _clearSessionRefresh() {
    if (this.refreshTimerId) {
      clearTimeout(this.refreshTimerId);
      this.refreshTimerId = null;
    }
  }

  /**
   * Notify listeners of auth state changes
   * @param {Object} event - The auth state change event
   * @private
   */
  _notifyListeners(event) {
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (err) {
        console.error("Error in auth state change listener:", err);
      }
    });
  }
}

export default new AuthService();